import { createContext, useContext, useEffect, useState } from "react";
import { Pin, PinOff } from "lucide-react";
import { toast } from "sonner";
import { PERIODS } from "@/lib/constants";

const PeriodCtx = createContext({
  period: "MTD",
  setPeriod: () => {},
  dept: "ALL",
  setDept: () => {},
  savedView: null,
  saveView: () => {},
  clearSavedView: () => {},
});

const STORAGE_KEY = "mativex:saved_view";

function loadSavedView() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function PeriodProvider({ children }) {
  const saved = typeof window !== "undefined" ? loadSavedView() : null;
  const [period, setPeriod] = useState(saved?.period || "MTD");
  const [dept, setDept] = useState(saved?.dept || "ALL");
  const [savedView, setSavedView] = useState(saved);

  const saveView = () => {
    const next = { period, dept };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setSavedView(next);
    toast.success(`Pinned · ${period} · ${dept === "ALL" ? "All depts" : dept}`);
  };

  const clearSavedView = () => {
    localStorage.removeItem(STORAGE_KEY);
    setSavedView(null);
    toast("Default view cleared");
  };

  return (
    <PeriodCtx.Provider value={{ period, setPeriod, dept, setDept, savedView, saveView, clearSavedView }}>
      {children}
    </PeriodCtx.Provider>
  );
}

export const usePeriod = () => useContext(PeriodCtx);

export function PeriodFilterBar() {
  const { period, setPeriod, dept, setDept, savedView, saveView, clearSavedView } = usePeriod();
  const [pulse, setPulse] = useState(false);

  // Pulse the pin icon briefly when current view diverges from saved
  useEffect(() => {
    if (!savedView) return;
    const diverged = savedView.period !== period || savedView.dept !== dept;
    if (diverged) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 400);
      return () => clearTimeout(t);
    }
  }, [period, dept, savedView]);

  const isPinnedExactly = savedView && savedView.period === period && savedView.dept === dept;

  return (
    <div className="flex items-center gap-2 w-full overflow-x-auto no-scrollbar">
      <div className="hairline flex shrink-0" data-testid="global-period-selector">
        {PERIODS.map((p, i) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            data-testid={`period-${p.toLowerCase()}`}
            className={`px-2.5 md:px-3 h-9 text-[11px] font-mono tracking-wider transition-colors whitespace-nowrap ${
              i > 0 ? "border-l border-border" : ""
            } ${period === p ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground"}`}
          >
            {p}
          </button>
        ))}
      </div>
      <select
        value={dept}
        onChange={(e) => setDept(e.target.value)}
        data-testid="department-filter"
        className="hairline h-9 px-2 text-[11px] font-mono bg-background tracking-wider shrink-0"
      >
        <option value="ALL">ALL DEPTS</option>
        <option>Sarees</option><option>Mens Shirts</option><option>Womens Kurti Set</option>
        <option>Mens Lower</option><option>Boys</option><option>Girls</option>
        <option>Mens Inner</option><option>Footwear</option>
      </select>

      {/* Save / pin view */}
      <button
        type="button"
        onClick={isPinnedExactly ? clearSavedView : saveView}
        data-testid="save-view-button"
        title={
          isPinnedExactly
            ? "Unpin default view"
            : savedView
            ? `Update default (currently ${savedView.period} · ${savedView.dept === "ALL" ? "All depts" : savedView.dept})`
            : "Pin this period + department as your default"
        }
        className={`hairline h-9 px-2.5 shrink-0 grid place-items-center transition-colors ${
          isPinnedExactly
            ? "text-primary border-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-secondary"
        } ${pulse ? "scale-110" : ""}`}
        style={{ transitionDuration: "180ms" }}
      >
        {isPinnedExactly ? <Pin className="w-3.5 h-3.5 fill-current" /> : <PinOff className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}
